import { registeredTabsHandlers } from '@/global/components/view-layouts/tabs-layout/common/registered-tabs-handlers'
import { dateService, FORMATS } from '@/global/services/date'
import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import { api } from '@/global/services/api'
import { makeFormData } from '../../helpers/formData'
import vehicleInsurancesGridBlock from './VehicleInsurancesGridBlock.vue'
import gridView from '@/global/components/grid/GridView.vue'
import vehicleInsuranceMixin from '../../mixins/vehicleInsuranceMixin'
import truncatedTextDisplay from '@/global/components/TruncatedTextDisplay.vue'
import { showDetailsAction, deleteAction, activationAction } from '@/global/components/table/helpers/tableActions'
import { getBooleanFromValue } from '@/global/utils/utils'

export default {
  name: 'Insurances',

  data () {
    return {
      showInsurancesTable: true,
      showInsurancesDetails: false,
      insuranceDetailsTitle: this.$t('fleet/vehicle.insurance'),
      insuranceDetailsBaseTitle: this.$t('fleet/vehicle.insurance'),
      insuranceEditState: false,
      insuranceCreateModeState: false,
      insuranceValidationErrors: {},
      insurancePermissions: {}
    }
  },

  mixins: [
    vehicleInsuranceMixin
  ],

  computed: {
    insurancesConfig () {
      return {
        title: this.$t('fleet/vehicle_insurance.insurances'),
        key: 'insurances',
        visible: getBooleanFromValue(this.permissions?.canReadVehicleInsurance),
        validationErrors: this.insuranceValidationErrors,
        onTabChange: () => {
          this.showMainEditButtons = false
        },
        editUpdate: (event) => {
          const { edit } = event || {}
          this.insuranceEditState = !!edit
        },
        createModeUpdate: (event) => {
          const { createMode } = event || {}
          this.insuranceCreateModeState = !!createMode
        },
        tabContent: [
          // table view
          {
            component: this.tableGridComponent,
            visible: this.showInsurancesTable,
            apiConfig: {
              get: {
                module: 'fleet',
                route: 'vehicle-insurance',
                queryParams: {
                  vehicle_id: this.$route.params.id
                }
              }
            },
            rowDblClick: (event) => this.onShowInsuranceDetails(event),
            mobileConfig: {
              title: this.$t('fleet/vehicle_insurance.insurances')
            },
            title_and_back_config: {
              title: this.$t('fleet/vehicle_insurance.insurances')
            },
            search_and_buttons_config: {
              newClick: () => this.onNewInsurance(),
              showNewBtn: getBooleanFromValue(this.insurancePermissions?.canCreate)
            },
            gridConfig: {
              component: gridView,
              blockConfig: {
                component: vehicleInsurancesGridBlock,
                showDetails: () => {
                  this.showInsurancesTable = false
                  this.showInsurancesDetails = true
                },
                setDetailsTitle: (val) => {
                  this.insuranceDetailsTitle = val
                }
              },
              useBorder: false
            },
            onDataReceivedWithParams: (event) => {
              this.insurancePermissions = event?.data?.meta?.permissions || {}
            },
            tableConfig: {
              cellsConfig: {
                type: {
                  custom_display_value: (item) => {
                    return item?.type?.description ? this.$t(item.type.description) : ''
                  }
                },
                currency: {
                  custom_display_value: (item) => {
                    return item?.currency?.name || ''
                  }
                },
                duration: {
                  style: {
                    textAlign: 'center'
                  }
                },
                start_date: {
                  custom_display_value: (item) => {
                    return item?.start_date ? dateService.formatFromUtc(item.start_date, FORMATS.date) : ''
                  }
                },
                expiry_date: {
                  custom_display_value: (item) => {
                    return item?.expiry_date ? dateService.formatFromUtc(item.expiry_date, FORMATS.date) : ''
                  }
                },
                alarm_on: {
                  style: {
                    justifyItems: 'center'
                  }
                },
                images: {
                  wrapperStyle: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'start'
                  }
                },
                pdf: {
                  wrapperStyle: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'start'
                  }
                }
              },
              headers: [
                {
                  label: this.$t('fleet/vehicle_insurance.insurance_type'),
                  key: 'type',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_insurance.contract_number'),
                  key: 'contract_number',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_insurance.foreign_id'),
                  key: 'foreign_id',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_insurance.insurance_provider'),
                  key: 'insurance_provider',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_insurance.franchise_details'),
                  key: 'franchise_details',
                  component: truncatedTextDisplay,
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_insurance.other_details'),
                  key: 'other_details',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_insurance.start_date'),
                  key: 'start_date',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_insurance.expiry_date'),
                  key: 'expiry_date',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_insurance.duration'),
                  key: 'duration',
                  style: {
                    textAlign: 'center'
                  },
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_insurance.payment_type'),
                  key: 'payment_type',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_insurance.monthly_instalment'),
                  key: 'monthly_instalment',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_insurance.currency'),
                  key: 'currency',
                  always_visible: true
                },
                {
                  label: this.$t('fleet/vehicle_insurance.images'),
                  key: 'images',
                  always_visible: true,
                  component: this.tableImageIconWithCounterComponent,
                  disableDefaultClick: true,
                  onClick: (event) => {
                    this.showFilesPreviewDialog({
                      ...event,
                      fieldKey: 'images',
                      removeApiConfig: {
                        module: 'fleet',
                        route: 'vehicle-insurance/remove-image/'
                      }
                    }, 'image', 'insurances')
                  }
                },
                {
                  label: this.$t('fleet/vehicle_insurance.pdf'),
                  key: 'pdf',
                  always_visible: true,
                  component: this.tableImageIconWithCounterComponent,
                  src: '/img/icons/table-pdf-icon.svg',
                  disableDefaultClick: true,
                  onClick: (event) => {
                    this.showFilesPreviewDialog({
                      ...event,
                      fieldKey: 'pdf',
                      removeApiConfig: {
                        module: 'fleet',
                        route: 'vehicle-insurance/remove-pdf/'
                      }
                    }, 'pdf', 'insurances')
                  }
                },
                {
                  label: this.$t('fleet/vehicle_insurance.status'),
                  key: 'status',
                  always_visible: true,
                  component: this.activeIconComponent
                },
                {
                  label: this.$t('fleet/vehicle_insurance.alarm_on'),
                  key: 'alarm_on',
                  always_visible: true,
                  component: this.cellIconComponent,
                  style: {
                    textAlign: 'center'
                  },
                  src: (event) => {
                    const { data } = event || {}
                    return data === 1 ? '/img/icons/green-check-circle.svg' : '/img/icons/grey-check-circle.svg'
                  }
                }
              ],
              actionsConfig: [
                showDetailsAction(this.$t, (event) => this.onShowInsuranceDetails(event), { visible: getBooleanFromValue(this.insurancePermissions?.canRead) }),
                activationAction(this.$t, (event) => this.onInsuranceActivation(event), (event) => !!event?.data?.status),
                deleteAction(this.$t, (event) => this.onInsuranceDelete(event), { visible: getBooleanFromValue(this.insurancePermissions?.canDelete) })
              ],
              paginationConfig: {
                perPageOptions: [50, 100]
              },
              filtersConfig: {
                fields: [
                  {
                    key: 'type',
                    label: this.$t('fleet/vehicle_insurance.insurance_type'),
                    type: 'autocomplete',
                    autocomplete_options: {
                      module: 'fleet',
                      route: 'vehicle-insurance/types',
                      custom_display_value: (item) => {
                        return item?.description ? this.$t(item.description) : ''
                      }
                    },
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'contract_number',
                    label: this.$t('fleet/vehicle_insurance.contract_number'),
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'foreign_id',
                    label: this.$t('fleet/vehicle_insurance.foreign_id'),
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'insurance_provider',
                    label: this.$t('fleet/vehicle_insurance.insurance_provider'),
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'franchise_details',
                    label: this.$t('fleet/vehicle_insurance.franchise_details'),
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'other_details',
                    label: this.$t('fleet/vehicle_insurance.other_details'),
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'start_date',
                    label: this.$t('fleet/vehicle_insurance.start_date'),
                    type: 'datetime-picker',
                    subType: 'date',
                    appendIcon: 'mdi-calendar-month',
                    format: FORMATS.date,
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'expiry_date',
                    label: this.$t('fleet/vehicle_insurance.expiry_date'),
                    type: 'datetime-picker',
                    subType: 'date',
                    appendIcon: 'mdi-calendar-month',
                    format: FORMATS.date,
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'duration',
                    label: this.$t('fleet/vehicle_insurance.duration'),
                    field_type: 'number',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'payment_type',
                    label: this.$t('fleet/vehicle_insurance.payment_type'),
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'monthly_instalment',
                    label: this.$t('fleet/vehicle_insurance.monthly_instalment'),
                    field_type: 'number',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'currency',
                    label: this.$t('fleet/vehicle_insurance.currency'),
                    type: 'autocomplete',
                    autocomplete_options: {
                      module: 'fleet',
                      route: 'vehicle-insurance/currency-types'
                    },
                    visible: true,
                    editable: true
                  }
                ]
              }
            }
          },
          // details view
          {
            component: this.tabActionsComponent,
            visible: this.showInsurancesDetails,
            title: this.insuranceDetailsTitle,
            showEditButtons: true,
            showEditBtn: getBooleanFromValue(this.insurancePermissions?.canUpdate),
            showBackButton: {
              onGoBack: () => this.exitFromInsuranceDetails()
            },
            onCancelTabSave: () => this.exitFromInsuranceDetails(false),
            onSaveClick: () => this.onSaveInsurance()
          },
          {
            component: this.columnsContentComponent,
            visible: this.showInsurancesDetails,
            showBackButton: {
              useChangesNotSavedDialog: false
            },
            rows: [
              {
                disabled: !getBooleanFromValue(this.insurancePermissions?.canUpdate),
                columnsCount: 4,
                fields: [
                  {
                    key: 'type',
                    label: this.$t('fleet/vehicle_insurance.insurance_type'),
                    type: 'autocomplete',
                    autocomplete_options: {
                      module: 'fleet',
                      route: 'vehicle-insurance/types',
                      custom_display_value: (item) => {
                        return this.$t(item.description)
                      }
                    },
                    visible: true,
                    editable: this.insuranceCreateModeState
                  },
                  {
                    key: 'contract_number',
                    label: this.$t('fleet/vehicle_insurance.contract_number'),
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'foreign_id',
                    label: this.$t('fleet/vehicle_insurance.foreign_id'),
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'insurance_provider',
                    label: this.$t('fleet/vehicle_insurance.insurance_provider'),
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'franchise_details',
                    label: this.$t('fleet/vehicle_insurance.franchise_details'),
                    type: 'textarea',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'other_details',
                    label: this.$t('fleet/vehicle_insurance.other_details'),
                    type: 'textarea',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'start_date',
                    label: this.$t('fleet/vehicle_insurance.start_date'),
                    type: 'datetime-picker',
                    subType: 'date',
                    appendIcon: 'mdi-calendar-month',
                    format: FORMATS.date,
                    onInput: (value, event) => {
                      const { updateValidationErrors } = event || {}

                      if (value && typeof updateValidationErrors === 'function' && !dateService.isValid(value, FORMATS.date, true)) {
                        updateValidationErrors({
                          start_date: this.$t('base.invalid_date').replace(':format', FORMATS.date)
                        })
                      }
                    },
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'expiry_date',
                    label: this.$t('fleet/vehicle_insurance.expiry_date'),
                    type: 'datetime-picker',
                    subType: 'date',
                    appendIcon: 'mdi-calendar-month',
                    format: FORMATS.date,
                    onInput: (value, event) => {
                      const { updateValidationErrors } = event || {}

                      if (value && typeof updateValidationErrors === 'function' && !dateService.isValid(value, FORMATS.date, true)) {
                        updateValidationErrors({
                          expiry_date: this.$t('base.invalid_date').replace(':format', FORMATS.date)
                        })
                      }
                    },
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'duration',
                    label: this.$t('fleet/vehicle_insurance.duration'),
                    field_type: 'number',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'images',
                    label: this.$t('fleet/vehicle/documents_tab.uploaded_images_label'),
                    type: 'files_preview',
                    subtype: 'image',
                    src: '/img/icons/table-image-icon.svg',
                    click: (event) => {
                      this.showFilesPreviewDialog({
                        ...event,
                        fieldKey: 'images',
                        removeApiConfig: {
                          module: 'fleet',
                          route: 'vehicle-insurance/remove-image/'
                        }
                      }, 'image', 'insurances')
                    },
                    visible: !this.insuranceEditState && !this.insuranceCreateModeState,
                    editable: true
                  },
                  {
                    key: 'payment_type',
                    label: this.$t('fleet/vehicle_insurance.payment_type'),
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'monthly_instalment',
                    label: this.$t('fleet/vehicle_insurance.monthly_instalment'),
                    field_type: 'number',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'currency',
                    label: this.$t('fleet/vehicle_insurance.currency'),
                    type: 'autocomplete',
                    autocomplete_options: {
                      module: 'fleet',
                      route: 'vehicle-insurance/currency-types'
                    },
                    visible: true,
                    editable: this.insuranceCreateModeState
                  },
                  {
                    key: 'images_upload',
                    label: this.$t('fleet/vehicle_insurance.images'),
                    type: 'files_upload',
                    subtype: 'image',
                    prependInnerIcon: 'mdi-camera',
                    src: '/img/icons/table-image-icon.svg',
                    accept: '/image*',
                    click: (event) => {
                      this.showFilesPreviewDialog({
                        ...event,
                        fieldKey: 'images_upload'
                      }, 'image', 'insurances')
                    },
                    visible: this.insuranceEditState || this.insuranceCreateModeState,
                    editable: true
                  },
                  {
                    key: 'pdf_upload',
                    label: this.$t('fleet/vehicle_insurance.pdf'),
                    type: 'files_upload',
                    subtype: 'pdf',
                    prependInnerIcon: 'mdi-camera',
                    accept: '.pdf',
                    src: '/img/icons/table-pdf-icon.svg',
                    click: (event) => {
                      this.showFilesPreviewDialog({
                        ...event,
                        fieldKey: 'pdf_upload'
                      }, 'pdf', 'insurances')
                    },
                    visible: this.insuranceEditState || this.insuranceCreateModeState,
                    editable: true
                  },
                  {
                    key: 'alarm_on',
                    label: this.$t('fleet/vehicle_insurance.alarm_on'),
                    type: 'base-switch',
                    visible: true,
                    editable: true
                  },
                  {
                    key: 'pdf',
                    label: this.$t('fleet/vehicle.uploaded_pdfs'),
                    type: 'files_preview',
                    subtype: 'pdf',
                    src: '/img/icons/table-pdf-icon.svg',
                    click: (event) => {
                      this.showFilesPreviewDialog({
                        ...event,
                        fieldKey: 'pdf',
                        removeApiConfig: {
                          module: 'fleet',
                          route: 'vehicle-insurance/remove-pdf/'
                        }
                      }, 'pdf', 'insurances')
                    },
                    visible: !this.insuranceEditState && !this.insuranceCreateModeState,
                    editable: true
                  }
                ]
              }
            ]
          }
        ]
      }
    }
  },

  methods: {
    onNewInsurance () {
      const { setCreateMode, clearData } = registeredTabsHandlers?.insurances || {}
      if (typeof setCreateMode === 'function') {
        setCreateMode(true)
      }
      if (typeof clearData === 'function') {
        clearData()
      }
      this.insuranceDetailsTitle = this.$t('fleet/vehicle.new_vehicle_insurance')
      this.showInsurancesTable = false
      this.showInsurancesDetails = true
    },

    exitFromInsuranceDetails (returnToTable = true) {
      const { clearData, setCreateMode, setEditMode, getCreateModeState, cancelSave } = registeredTabsHandlers?.insurances || {}
      let isCreateMode = false
      if (typeof getCreateModeState === 'function') {
        isCreateMode = getCreateModeState()
      }

      if (isCreateMode) {
        this.showInsurancesTable = true
        this.showInsurancesDetails = false

        if (typeof clearData === 'function') {
          clearData()
        }
        if (typeof setCreateMode === 'function') {
          setCreateMode(false)
        }
        if (typeof setEditMode === 'function') {
          setEditMode(false)
        }
      }
      else if (typeof cancelSave === 'function') {
        // return to table view
        if (returnToTable) {
          this.showInsurancesTable = true
          this.showInsurancesDetails = false
        }

        cancelSave()
      }
      this.insuranceValidationErrors = {}
    },

    async onSaveInsurance () {
      try {
        const { getFormData } = registeredTabsHandlers?.insurances || {}
        const { fetchData } = registeredTableHandlers?.insurances || {}
        const { id: vehicleId } = this.$route?.params || {}

        if (!vehicleId) return

        const data = typeof getFormData === 'function' ? getFormData()?.data : {}
        const formData = makeFormData(data,
          ['images_upload', 'pdf_upload'],
          {
            vehicle_id: vehicleId
          },
          {
            start_date: {
              fromFormat: FORMATS.date,
              toFormat: FORMATS.isoDate
            },
            expiry_date: {
              fromFormat: FORMATS.date,
              toFormat: FORMATS.isoDate
            }
          })

        let apiUrl = 'vehicle-insurance'
        const { id: insuranceId } = data || {}
        if (!this.insuranceCreateModeState && insuranceId) {
          formData.append('_method', 'PATCH')
          apiUrl += `/${insuranceId}`
        }

        await api('fleet').post(apiUrl, formData)
        this.exitFromInsuranceDetails()

        if (typeof fetchData === 'function') {
          fetchData()
        }
      }
      catch (exception) {
        this.insuranceValidationErrors = exception?.response?.data?.errors || {}
        console.error(exception)
      }
    }
  }
}
